import { ConfigProvider, TableProps } from "antd";
import AntTable from "antd/es/table";
import type { ColumnsType, ColumnType } from "antd/es/table";
import {
  SummaryCalculationService,
  SummaryConfig,
} from "./summaryCalculations";

type Prop<T extends object> = {
  columns: ColumnsType<T>;
  dataSource: T[];
  pagination?: false | TableProps["pagination"];
  rowKey: string;
  rowSelectionType?: "checkbox" | "radio";
  onRowSelect?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void;
  size?: "small" | "middle" | "large";
  bordered?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  loading?: boolean;
  rowHoverable?: boolean;
  showHeader?: boolean;
  className?: string;
  rowClassName?: string;
  scroll?: {
    scrollToFirstRowOnChange?: boolean;
    x?: true | number | string;
    y?: number | string;
  };
  nullText?: string;
  onRow?: any;
  borderColor?: string;
  summary?: SummaryConfig<T>;
};

export const TableWithSummary = <T extends object>(props: Prop<T>) => {
  const {
    scroll = { x: true },
    borderColor = "#fff",
    nullText = " - ",
    summary,
    ...otherProps
  } = props;

  const enhancedColumns = otherProps.columns.map((col) => {
    if (!col.render) {
      return {
        ...col,
        render: (text: any) =>
          text === null || text === undefined || text === "" ? (
            <span className=" text-red-700 ">{nullText}</span>
          ) : (
            text
          ),
      };
    } else {
      return col;
    }
  });

  const isRegularColumn = (column: any): column is ColumnType<T> => {
    return column && typeof column.dataIndex !== "undefined";
  };

  const generateSummary = (data: readonly T[]): React.ReactNode => {
    if (!summary?.enabled || !data?.length) return null;

    const {
      calculations,
      rowLabels,
      excludeColumns = [],
      labelColumn,
      sticky = true,
    } = summary;

    const allData = otherProps?.dataSource || [];

    const summaryService = new SummaryCalculationService(
      allData,
      summary,
      (value: any, includeScale?: boolean) => {
        if (typeof value === "number") {
          if (value === null || value === undefined || isNaN(value)) {
            return nullText;
          }
          return value.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        return value || nullText;
      }
    );

    const summaryData = summaryService.calculateSummaryData();
    const summaryRows: React.ReactNode[] = [];

    const renderSummaryCell = (
      columnKey: keyof T,
      value: any,
      originalColumn: ColumnType<T>
    ) => {
      if (originalColumn?.render && typeof value === "number") {
        const mockRecord = { [columnKey]: value } as T;
        return originalColumn.render(value, mockRecord, -1);
      }

      if (typeof value === "number") {
        if (value === null || value === undefined || isNaN(value)) {
          return nullText;
        }
        return value.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      return value || nullText;
    };

    const rowClassName = sticky
      ? "summary-row summary-row-sticky font-semibold bg-gray-50"
      : "summary-row font-semibold bg-gray-50";

    summaryData.forEach((summaryRowData, rowIndex) => {
      const summaryType =
        summaryRowData[String(labelColumn)] || summaryRowData["Summary_Type"];

      let rowKey = `summary-${rowIndex}`;
      let labelColor = "text-gray-600";

      if (summaryType?.includes("Average")) {
        rowKey = "average";
        labelColor = "text-gray-600";
      } else if (summaryType?.includes("Median")) {
        rowKey = "median";
        labelColor = "text-gray-600";
      } else if (summaryType?.includes("Sum")) {
        rowKey = "sum";
        labelColor = "text-purple-600";
      } else if (summaryType?.includes("Count")) {
        rowKey = "count";
        labelColor = "text-orange-600";
      } else {
        labelColor = "text-indigo-600";
      }

      summaryRows.push(
        <AntTable.Summary.Row key={rowKey} className={rowClassName}>
          {enhancedColumns.map((col, index) => {
            if (!isRegularColumn(col)) {
              return (
                <AntTable.Summary.Cell
                  key={`col-${index}`}
                  index={index}
                ></AntTable.Summary.Cell>
              );
            }

            const columnKey = col.dataIndex as keyof T;

            if (columnKey === labelColumn) {
              return (
                <AntTable.Summary.Cell key={columnKey as string} index={index}>
                  <span className={`font-bold ${labelColor}`}>
                    {summaryType}
                  </span>
                </AntTable.Summary.Cell>
              );
            }

            const calculatedValue = summaryRowData[String(columnKey)];
            if (
              calculatedValue !== undefined &&
              !excludeColumns.includes(columnKey)
            ) {
              return (
                <AntTable.Summary.Cell key={columnKey as string} index={index}>
                  {renderSummaryCell(columnKey, calculatedValue, col)}
                </AntTable.Summary.Cell>
              );
            }

            return (
              <AntTable.Summary.Cell
                key={columnKey as string}
                index={index}
              ></AntTable.Summary.Cell>
            );
          })}
        </AntTable.Summary.Row>
      );
    });

    return summaryRows.length > 0 ? <>{summaryRows}</> : null;
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: "#F0F6FF",
            headerColor: "#667085",
            cellPaddingBlock: 10,
            colorInfoText: "#667085",
            borderColor: "#ebecef",
          },
          Pagination: {
            colorPrimary: "#FFF",
            colorText: "#667085",
          },
        },
      }}
    >
      <div className="table-container rounded-[4px]">
        {/* These selectors have no page-scope prefix (this table has no
            way to receive a scope class from its callers), but "summary-row"
            / "summary-row-sticky" / "table-container" are unique to this
            component, so that's safe. Colors read the shared --vi-* tokens
            (screener.theme.css imports these transitively via the app
            bundle, same as every other themed page) instead of the
            previous light-only literals (#f9fafb / #e5e7eb), which used to
            leave the sticky summary row a fixed near-white band in dark
            mode.

            The summary row is pinned via `position: sticky; bottom: 0`
            (see .summary-row-sticky / .ant-table-summary below), stacking
            it over whichever body row has scrolled underneath — so its
            background must be fully OPAQUE, not the translucent --vi-surface
            token used elsewhere on this page (a low-alpha overlay), or the
            row scrolled behind it bleeds through as ghosted text. Mixed
            against the opaque --vi-card-bg instead, same fix as the fixed-
            column header cells in screener.theme.css. */}
        <style>{`
          .summary-row {
            background-color: color-mix(in srgb, var(--vi-text-tertiary) 6%, var(--vi-card-bg)) !important;
            border-top: 2px solid var(--vi-card-border) !important;
          }
          .summary-row:hover {
            background-color: color-mix(in srgb, var(--vi-text-tertiary) 6%, var(--vi-card-bg)) !important;
          }
          .summary-row td {
            color: var(--vi-text) !important;
            font-weight: 600 !important;
             text-align: right !important;
          }
          .summary-row td:first-child {
            text-align: left !important;
          }
          .summary-row-sticky {
            position: sticky !important;
            bottom: 0 !important;
            z-index: 10 !important;

          }
          /* Ensure sticky summary works with scrollable table */
          .ant-table-tbody > tr:last-child.summary-row-sticky td {
            border-bottom: none !important;
          }
          /* Handle horizontal scrolling with sticky summary */
          .table-container .ant-table-summary {
            position: sticky !important;
            bottom: 0 !important;
            z-index: 10 !important;
            background-color: color-mix(in srgb, var(--vi-text-tertiary) 6%, var(--vi-card-bg)) !important;
            box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.35) !important;
          }
        `}</style>
        <AntTable<T>
          columns={enhancedColumns}
          className="custom-table"
          rowClassName={props?.rowClassName}
          dataSource={otherProps?.dataSource}
          scroll={scroll}
          rowKey={otherProps.rowKey}
          size={otherProps.size}
          pagination={otherProps.pagination}
          bordered={otherProps.bordered}
          loading={otherProps.loading}
          rowHoverable={otherProps.rowHoverable}
          onRow={props.onRow}
          showHeader={otherProps.showHeader}
          summary={generateSummary}
          {...(otherProps.rowSelectionType && {
            rowSelection: {
              type: otherProps.rowSelectionType,
              onChange: otherProps.onRowSelect,
            },
          })}
          {...(otherProps.footer && {
            footer: () => otherProps.footer,
          })}
          {...(otherProps.header && {
            title: () => otherProps.header,
          })}
        />
      </div>
    </ConfigProvider>
  );
};
